import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../context/ThemeContext';

const MineralTreemap = ({ data, loading, error, onMineralSelect }) => {
  const { isDarkMode } = useTheme();
  const svgRef = useRef();
  const containerRef = useRef();

  // Transform mineral data to D3 hierarchical format
  const transformToD3Format = (mineralData) => {
    if (!mineralData || mineralData.length === 0) {
      return { name: "Minerals", children: [] };
    }

    // Filter to only show specific minerals
    const allowedMinerals = [
      'Lithium', 'Cobalt', 'Nickel', 'Graphite', 
      'Rare Earth Elements', 'Rare Earth', 'Rare Earths',
      'Rare Earth Element', 'REE'
    ];
    
    const filteredData = mineralData.filter(item => {
      const mineralName = item.mineral;
      return allowedMinerals.some(allowed => 
        mineralName === allowed || 
        mineralName.toLowerCase().includes(allowed.toLowerCase()) ||
        allowed.toLowerCase().includes(mineralName.toLowerCase())
      );
    });

    const children = filteredData.map(item => ({
      name: item.mineral,
      value: item.count,
      mineral: item.mineral,
      count: item.count
    }));

    return {
      name: "Minerals",
      children: children
    };
  };

  // Optimized D3 Treemap rendering function
  const renderTreemap = useCallback((data) => {
    if (!containerRef.current || !data || !data.children || data.children.length === 0) {
      return;
    }

    // Clear previous content
    d3.select(containerRef.current).selectAll("*").remove();

    const width = 400;
    const height = 300;
    
    const svg = d3.select(containerRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("width", "100%")
      .style("height", "100%");

    // Create treemap layout
    const treemap = d3.treemap()
      .size([width, height])
      .padding(2)
      .round(true);

    // Create hierarchy and calculate layout
    const root = d3.hierarchy(data)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    treemap(root);

    // Color scale - use blue scheme to match Trade Balance styling
    const maxValue = d3.max(root.leaves(), d => d.data.value);
    const minValue = d3.min(root.leaves(), d => d.data.value);
    
    const colorScale = (value) => {
      if (maxValue === minValue) {
        return d3.interpolateBlues(0.7);
      }
      
      const normalizedValue = (value - minValue) / (maxValue - minValue);
      const adjustedValue = Math.pow(normalizedValue, 0.7);
      const colorValue = 0.4 + (adjustedValue * 0.6);
      
      return d3.interpolateBlues(colorValue);
    };
    
    // Helper function to determine text color based on background brightness
    const getTextColor = (backgroundColor) => {
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#1f2937' : '#ffffff';
    };

    // Create tooltip div
    const tooltip = d3.select("body").selectAll(".d3-mineral-treemap-tooltip").data([null]);
    const tooltipEnter = tooltip.enter().append("div").attr("class", "d3-mineral-treemap-tooltip");
    const tooltipUpdate = tooltipEnter.merge(tooltip)
      .style("position", "absolute")
      .style("background", isDarkMode ? "#1f2937" : "#ffffff")
      .style("color", isDarkMode ? "#ffffff" : "#1f2937")
      .style("padding", "12px")
      .style("border-radius", "8px")
      .style("font-size", "12px")
      .style("font-family", "Inter, system-ui, sans-serif")
      .style("pointer-events", "none")
      .style("z-index", "1000")
      .style("border", isDarkMode ? "1px solid #374151" : "1px solid #d1d5db")
      .style("box-shadow", "0 4px 6px -1px rgba(0, 0, 0, 0.1)")
      .style("opacity", 0);

    // Create groups for each leaf
    const leaf = svg.selectAll("g")
      .data(root.leaves())
      .enter().append("g")
      .attr("transform", d => `translate(${d.x0},${d.y0})`);

    // Add rectangles
    leaf.append("rect")
      .attr("width", d => d.x1 - d.x0)
      .attr("height", d => d.y1 - d.y0)
      .attr("fill", d => colorScale(d.data.value))
      .attr("stroke", "#374151")
      .attr("stroke-width", 0.5)
      .attr("rx", 2)
      .style("cursor", "pointer")
      .on("click", function(event, d) {
        // Handle mineral selection
        const mineral = d.data.mineral || d.data.name;
        if (onMineralSelect) {
          onMineralSelect(mineral);
        }
      })
      .on("mouseover", function(event, d) {
        // Highlight rectangle
        d3.select(this)
          .attr("stroke", "#ffffff")
          .attr("stroke-width", 2);
        
        // Show tooltip
        const displayName = d.data.mineral || d.data.name;
        const titleColor = isDarkMode ? "#ffffff" : "#1f2937";
        const labelColor = isDarkMode ? "#a3a3a3" : "#6b7280";
        
        const tooltipContent = `
          <div><strong style="color: ${titleColor}; font-size: 14px;">${displayName}</strong></div>
          <div style="margin-top: 8px;">
            <div style="color: ${labelColor};">News Articles: <span style="color: #3b82f6; font-weight: bold;">${d.data.count}</span></div>
            <div style="color: ${labelColor}; font-size: 11px; margin-top: 4px;">Click to view PESTLE news</div>
          </div>
        `;
        
        tooltipUpdate
          .html(tooltipContent)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 10) + "px")
          .transition()
          .duration(200)
          .style("opacity", 1);
      })
      .on("mouseout", function() {
        // Remove highlight
        d3.select(this)
          .attr("stroke", "#374151")
          .attr("stroke-width", 0.5);
        
        // Hide tooltip
        tooltipUpdate.transition()
          .duration(300)
          .style("opacity", 0);
      });

    // Add mineral labels
    leaf.append("text")
      .attr("x", 4)
      .attr("y", 18)
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .attr("fill", d => {
        const backgroundColor = colorScale(d.data.value);
        return getTextColor(backgroundColor);
      })
      .attr("font-family", "Inter, system-ui, sans-serif")
      .style("text-shadow", d => {
        const backgroundColor = colorScale(d.data.value);
        const textColor = getTextColor(backgroundColor);
        return textColor === '#ffffff' ? "1px 1px 2px rgba(0,0,0,0.8)" : "1px 1px 2px rgba(255,255,255,0.8)";
      })
      .text(d => {
        const width = d.x1 - d.x0;
        const mineral = d.data.mineral || d.data.name;
        
        // Only show label if rectangle is wide enough
        if (width > 60) {
          return mineral.length > 12 ? mineral.substring(0, 12) + '...' : mineral;
        }
        return "";
      });

    // Add count labels
    leaf.append("text")
      .attr("x", 4)
      .attr("y", 36)
      .attr("font-size", "16px")
      .attr("fill", d => {
        const backgroundColor = colorScale(d.data.value);
        return getTextColor(backgroundColor);
      })
      .attr("font-family", "Inter, system-ui, sans-serif")
      .attr("opacity", 0.9)
      .style("text-shadow", d => {
        const backgroundColor = colorScale(d.data.value);
        const textColor = getTextColor(backgroundColor);
        return textColor === '#ffffff' ? "1px 1px 2px rgba(0,0,0,0.8)" : "1px 1px 2px rgba(255,255,255,0.8)";
      })
      .text(d => {
        const width = d.x1 - d.x0;
        const height = d.y1 - d.y0;
        
        // Only show count if rectangle is big enough
        if (width > 50 && height > 50) {
          return `${d.data.count} articles`;
        }
        return "";
      });
  }, [isDarkMode]);

  // Render treemap when data changes
  useEffect(() => {
    if (data && !loading && !error) {
      const d3Data = transformToD3Format(data);
      renderTreemap(d3Data);
    }
  }, [data, loading, error, renderTreemap]);

  return (
    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
      {/* Treemap Title */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
          News Articles by Mineral
        </h3>
      </div>
      
      {/* Treemap Container */}
      <div className="h-80 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-200 dark:bg-gray-700 transition-colors duration-200">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-500 mx-auto mb-2"></div>
              <div className="text-gray-600 dark:text-gray-300 text-sm">Loading mineral data...</div>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-600 dark:text-red-400">
              <div className="text-lg mb-2">⚠️</div>
              <div className="text-sm">Error loading mineral data</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{error}</div>
            </div>
          </div>
        ) : data && data.length > 0 ? (
          <svg 
            ref={containerRef} 
            className="w-full h-full"
          ></svg>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <div className="text-lg mb-2">📊</div>
              <div className="text-sm">No mineral data available</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MineralTreemap;
